/**
 * AI Provider Management — health, active provider, fallback, metrics
 */
import React, { useState, useCallback } from "react";
import { providers as providersApi } from "../api.js";
import { useAsync, useAutoRefresh } from "../hooks.js";

export function ProvidersView() {
  const [switching, setSwitching] = useState<"primary" | "fallback" | null>(null);
  const [selectedType, setSelectedType] = useState("");
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState<string | null>(null);
  const [toast, setToast]       = useState<string | null>(null);

  const listFn  = useCallback(() => providersApi.list(), []);
  const metrFn  = useCallback(() => providersApi.metrics(), []);

  const { data: listData, loading, error, reload } = useAsync<any>(listFn, []);
  const { data: metrData } = useAsync<any>(metrFn, []);

  useAutoRefresh(reload, 30_000);

  const providers: any[] = listData?.providers ?? [];

  async function doSwitch() {
    if (!switching || !selectedType) return;
    setBusy(true); setErr(null);
    try {
      if (switching === "primary")  await providersApi.setPrimary(selectedType);
      if (switching === "fallback") await providersApi.setFallback(selectedType);
      setToast(`${switching === "primary" ? "Primary" : "Fallback"} provider set to ${selectedType}`);
      setTimeout(() => setToast(null), 4000);
      setSwitching(null); reload();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <div>
          <h1 className="ops-page-title">AI Providers</h1>
          <p className="ops-page-subtitle">
            Active: <strong>{listData?.activeProvider ?? "—"}</strong>
            {listData?.fallbackProvider && <> · Fallback: <strong>{listData.fallbackProvider}</strong></>}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className="ops-btn ops-btn-secondary ops-btn-sm"
            onClick={() => { setSelectedType(listData?.activeProvider ?? "mock"); setSwitching("primary"); }}>
            Switch Primary
          </button>
          <button className="ops-btn ops-btn-secondary ops-btn-sm"
            onClick={() => { setSelectedType(listData?.fallbackProvider ?? "mock"); setSwitching("fallback"); }}>
            Set Fallback
          </button>
          <button className="ops-btn ops-btn-secondary ops-btn-sm" onClick={reload}>↻</button>
        </div>
      </div>

      {toast && (
        <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid var(--ops-success)",
          borderRadius: "var(--ops-radius)", padding: "0.625rem 1rem", marginBottom: "1rem",
          color: "var(--ops-success)", fontSize: "0.8125rem" }}>
          ✓ {toast}
        </div>
      )}
      {error && <div className="ops-error">{error}</div>}
      {loading && <div className="ops-loading">Loading…</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1.5rem" }}>
        {providers.map((p: any) => (
          <div key={p.type} className="ops-card" style={{ display: "flex", alignItems: "center", gap: "1.25rem" }}>
            <div style={{ minWidth: 150 }}>
              <div style={{ fontWeight: 700 }}>{p.name}</div>
              <div style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>{p.type}</div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              {p.isActive   && <span className="ops-badge ops-badge-green">Active</span>}
              {p.isFallback && <span className="ops-badge ops-badge-blue">Fallback</span>}
              {p.configured
                ? <span className="ops-badge ops-badge-green">Configured</span>
                : <span className="ops-badge ops-badge-neutral">Not configured</span>}
            </div>
            {p.metrics && (
              <div style={{ display: "flex", gap: "1.25rem", marginLeft: "auto", fontSize: "0.8125rem" }}>
                <div><strong>{p.metrics.reqs ?? 0}</strong><div style={{ fontSize: "0.7rem", color: "var(--ops-muted)" }}>reqs (24h)</div></div>
                <div><strong>{p.metrics.fails ?? 0}</strong><div style={{ fontSize: "0.7rem", color: "var(--ops-muted)" }}>fails</div></div>
                <div><strong>{Math.round(p.metrics.latency ?? 0)} ms</strong><div style={{ fontSize: "0.7rem", color: "var(--ops-muted)" }}>avg latency</div></div>
                <div><strong>¢{((p.metrics.cost ?? 0) / 100).toFixed(2)}</strong><div style={{ fontSize: "0.7rem", color: "var(--ops-muted)" }}>cost (24h)</div></div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Historical metrics */}
      {metrData?.metrics?.length > 0 && (
        <>
          <p className="ops-card-title">Provider Metrics — last 7 days</p>
          <div className="ops-table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Provider</th><th>Period</th><th>Requests</th><th>Failures</th>
                  <th>Tokens</th><th>Avg latency</th><th>Cost (¢)</th>
                </tr>
              </thead>
              <tbody>
                {metrData.metrics.map((m: any, i: number) => (
                  <tr key={i}>
                    <td><span className="ops-badge ops-badge-purple">{m.provider_type}</span></td>
                    <td style={{ fontSize: "0.7rem", color: "var(--ops-muted)" }}>{m.period_start?.slice(0,10)}</td>
                    <td>{m.total_requests}</td>
                    <td style={{ color: m.failed_requests > 0 ? "var(--ops-danger)" : "inherit" }}>
                      {m.failed_requests}
                    </td>
                    <td>{m.total_tokens?.toLocaleString()}</td>
                    <td>{Math.round(m.avg_latency_ms)} ms</td>
                    <td>{(m.estimated_cost_cents / 100).toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {switching && (
        <div className="ops-modal-overlay" onClick={() => setSwitching(null)}>
          <div className="ops-modal" onClick={e => e.stopPropagation()}>
            <h2 className="ops-modal-title">
              Set {switching === "primary" ? "Primary" : "Fallback"} Provider
            </h2>
            <div className="ops-form-group">
              <label className="ops-form-label">Provider</label>
              <select className="ops-select" style={{ width: "100%" }}
                value={selectedType} onChange={e => setSelectedType(e.target.value)}>
                {providers.filter(p => p.configured).map(p => (
                  <option key={p.type} value={p.type}>{p.name}</option>
                ))}
              </select>
            </div>
            <p style={{ fontSize: "0.8125rem", color: "var(--ops-muted)", marginTop: 0 }}>
              The change takes effect on the next AI request. No redeployment needed.
            </p>
            {err && <div className="ops-error">{err}</div>}
            <div className="ops-modal-actions">
              <button className="ops-btn ops-btn-secondary" onClick={() => setSwitching(null)}>Cancel</button>
              <button className="ops-btn ops-btn-primary" disabled={busy} onClick={doSwitch}>
                {busy ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
